/**
 * RbacAuditDashboard.jsx
 * =======================
 *
 * Dashboard admin en lecture seule pour auditer le RBAC V2 :
 *   - Statistiques (X perms cataloguées, Y rôles actifs, Z utilisateurs)
 *   - Matrice rôles × permissions (avec compteurs et phantom perms)
 *   - Preview "Voir comme [rôle]" — affiche la liste de perms d'un rôle simulé
 *   - 403 récents avec contexte
 *
 * Aucune action mutative. Gate backend : `roles.view` ou commercial/finance/auditeur.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Shield, Search, Eye, AlertTriangle, Users, Key, Layers, RefreshCw, Lock, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RequirePermission, PermissionDenied } from '../../components/permissions/RequirePermission';
import { usePermissions } from '../../contexts/PermissionsContext';
import { PERMS } from '../../utils/permissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/rbac-audit`;

// ===== Sub-components =====
const StatTile = ({ icon: Icon, label, value, color = 'emerald', testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${color}-50 text-${color}-600`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 truncate">{label}</p>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const RoleMatrixTable = ({ roles, filter, onFilterChange }) => {
  const filtered = useMemo(() => {
    const q = filter.toLowerCase().trim();
    if (!q) return roles;
    return roles.filter(r => r.slug.toLowerCase().includes(q) || (r.name || '').toLowerCase().includes(q));
  }, [roles, filter]);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
        <Input
          className="pl-9"
          placeholder="Filtrer un rôle (admin, finance, ops…)"
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          data-testid="role-matrix-filter"
        />
      </div>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-3 font-semibold text-slate-700">Rôle</th>
              <th className="text-left p-3 font-semibold text-slate-700">Description</th>
              <th className="text-center p-3 font-semibold text-slate-700">Permissions</th>
              <th className="text-center p-3 font-semibold text-slate-700">Modules</th>
              <th className="text-center p-3 font-semibold text-slate-700">Statut</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.slug} className="border-b hover:bg-slate-50" data-testid={`role-row-${r.slug}`}>
                <td className="p-3">
                  <div className="font-mono font-semibold text-slate-900">{r.slug}</div>
                  {r.name && r.name !== r.slug && <div className="text-xs text-slate-500">{r.name}</div>}
                </td>
                <td className="p-3 text-slate-600 max-w-md truncate">{r.description || <em className="text-slate-400">—</em>}</td>
                <td className="p-3 text-center">
                  <Badge variant="outline" className="font-mono">{r.permissionsCount}</Badge>
                  {r.phantomPermissionsCount > 0 && (
                    <div className="text-xs text-amber-700 mt-1 flex items-center justify-center gap-1" title={r.phantomPermissions.join(', ')}>
                      <AlertTriangle className="w-3 h-3" />
                      {r.phantomPermissionsCount} phantom
                    </div>
                  )}
                </td>
                <td className="p-3 text-center text-slate-600">{r.modules.length}</td>
                <td className="p-3 text-center">
                  {r.isSystem && <Badge variant="secondary" className="mr-1">Système</Badge>}
                  {r.isActive ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Actif</Badge>
                  ) : (
                    <Badge variant="destructive">Inactif</Badge>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center text-slate-500 italic py-8">Aucun rôle ne correspond.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RolePreviewPanel = ({ availableRoles }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');

  const loadPreview = async (slug) => {
    if (!slug) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/preview-permissions`, { params: { role: slug } });
      setPreview(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur de chargement');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const groupedPerms = useMemo(() => {
    if (!preview) return {};
    const g = {};
    preview.permissions.forEach((p) => {
      const mod = p.split('.', 1)[0];
      g[mod] = g[mod] || [];
      g[mod].push(p);
    });
    return g;
  }, [preview]);

  const filteredModules = useMemo(() => {
    const mods = Object.keys(groupedPerms).sort();
    if (!moduleFilter.trim()) return mods;
    const q = moduleFilter.toLowerCase();
    return mods.filter(m => m.includes(q));
  }, [groupedPerms, moduleFilter]);

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="preview-safety-notice">
        <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Mode lecture seule — aucune impersonation côté serveur.</strong> Cet outil affiche uniquement les permissions
          déclarées en BDD pour le rôle sélectionné. Votre identité réelle reste inchangée.
        </div>
      </div>
      <div className="flex gap-3 items-end flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-semibold text-slate-700 block mb-1">Rôle à prévisualiser</label>
          <Select value={selectedRole} onValueChange={(v) => { setSelectedRole(v); loadPreview(v); }}>
            <SelectTrigger data-testid="preview-role-select">
              <SelectValue placeholder="Choisir un rôle…" />
            </SelectTrigger>
            <SelectContent>
              {availableRoles.map((r) => (
                <SelectItem key={r.slug} value={r.slug}>{r.name || r.slug} ({r.permissionsCount} perms)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" disabled={!selectedRole || loading} onClick={() => loadPreview(selectedRole)} data-testid="preview-reload-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Recharger
        </Button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {preview && (
        <div className="space-y-3" data-testid="preview-result">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile icon={Key} label="Permissions" value={preview.count} color="emerald" />
            <StatTile icon={Layers} label="Modules" value={preview.modules.length} color="blue" />
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500">Admin bypass</p>
                <p className="text-2xl font-bold text-slate-900">{preview.isAdmin ? 'Oui' : 'Non'}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500">Rôle</p>
                <p className="text-base font-bold text-slate-900 truncate">{preview.role}</p>
              </CardContent>
            </Card>
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="Filtrer par module (finance, operations…)"
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              data-testid="preview-module-filter"
            />
          </div>
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
            {filteredModules.map((mod) => (
              <Card key={mod}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm uppercase tracking-wider text-slate-600 flex items-center gap-2">
                    {mod}
                    <Badge variant="outline" className="text-xs">{groupedPerms[mod].length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex flex-wrap gap-1.5">
                    {groupedPerms[mod].map((p) => (
                      <code key={p} className="px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded font-mono">{p}</code>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredModules.length === 0 && <p className="text-sm text-slate-500 italic">Aucun module ne correspond.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

// ===== Main Component =====
const RbacAuditDashboardInner = () => {
  const [stats, setStats] = useState(null);
  const [matrix, setMatrix] = useState([]);
  const [denied, setDenied] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statsRes, matrixRes, deniedRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/role-matrix`),
        axios.get(`${API}/recent-denied?since_hours=72`),
      ]);
      setStats(statsRes.data);
      setMatrix(matrixRes.data.roles || []);
      setDenied(deniedRes.data.entries || []);
    } catch (e) {
      console.error('RBAC Audit load error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="rbac-audit-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-emerald-600" />
            Audit RBAC V2
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Vue d'ensemble lecture seule de la matrice rôles × permissions et des 403 récents.
          </p>
        </div>
        <Button variant="outline" onClick={loadAll} disabled={loading} data-testid="rbac-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Rafraîchir
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile icon={Key} label="Permissions cataloguées" value={stats.totalPermissions} color="emerald" testId="stat-perms" />
          <StatTile icon={Shield} label="Rôles actifs" value={`${stats.activeRoles} / ${stats.totalRoles}`} color="blue" testId="stat-roles" />
          <StatTile icon={Users} label="Utilisateurs" value={stats.totalUsers} color="violet" testId="stat-users" />
          <StatTile icon={Layers} label="Modules métier" value={stats.modules.length} color="amber" testId="stat-modules" />
        </div>
      )}

      <Tabs defaultValue="matrix">
        <TabsList>
          <TabsTrigger value="matrix" data-testid="tab-matrix">Matrice rôles × perms</TabsTrigger>
          <TabsTrigger value="preview" data-testid="tab-preview">
            <Eye className="w-4 h-4 mr-1" /> Voir comme [rôle]
          </TabsTrigger>
          <TabsTrigger value="denied" data-testid="tab-denied">
            403 récents
            {denied.length > 0 && <Badge variant="destructive" className="ml-2">{denied.length}</Badge>}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="matrix" className="mt-4">
          <RoleMatrixTable roles={matrix} filter={filter} onFilterChange={setFilter} />
        </TabsContent>
        <TabsContent value="preview" className="mt-4">
          <RolePreviewPanel availableRoles={matrix} />
        </TabsContent>
        <TabsContent value="denied" className="mt-4">
          {denied.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Shield className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p>Aucun refus d'accès enregistré dans les 72 dernières heures.</p>
              <p className="text-xs mt-2">Cela signifie que personne n'a tenté une action non autorisée, ou que le logging RBAC est désactivé.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {denied.map((d, i) => (
                <Card key={i} data-testid={`denied-entry-${i}`}>
                  <CardContent className="p-3 text-sm flex items-start gap-3">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs text-slate-700">{d.action || d.event_type}</p>
                      <p className="text-slate-600 truncate">{d.detail || d.message || JSON.stringify(d).slice(0, 200)}</p>
                      {d.createdAt && <p className="text-xs text-slate-400 mt-1">{new Date(d.createdAt).toLocaleString('fr-FR')}</p>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const RbacAuditDashboard = () => {
  const { hasPermission, isAdmin } = usePermissions();
  if (!isAdmin && !hasPermission(PERMS.ROLES_VIEW)) {
    return <div className="p-6"><PermissionDenied permission={PERMS.ROLES_VIEW} title="Audit RBAC réservé" /></div>;
  }
  return (
    <RequirePermission permission={PERMS.ROLES_VIEW}>
      <RbacAuditDashboardInner />
    </RequirePermission>
  );
};

export default RbacAuditDashboard;
